import React, { useState, useRef, useEffect } from 'react';
import Button from '../common/Button';
import { XCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline'; // For success/remove icons
import imageCompression from 'browser-image-compression';

// Simple UUID generator for internal use
const generateSimpleId = () => Math.random().toString(36).substr(2, 9);

// Simple async delay
// const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Image compression configuration
const DEFAULT_COMPRESSION_OPTIONS = {
  maxSizeMB: 0.3,            // Maximum file size in MB (reduced from 1MB to 300KB)
  maxWidthOrHeight: 1500,    // Maximum width or height in pixels (reduced from 1920px)
  useWebWorker: true,        // Use web worker for better performance
  quality: 0.85,             // Quality from 0.1 to 1 (slightly increased for better quality)
  preserveExif: false,       // Remove EXIF data to reduce size
  initialQuality: 0.9,       // Initial quality for first compression attempt
  alwaysKeepResolution: false, // Allow resolution reduction for better compression
  fileType: 'image/jpeg'     // Convert to JPEG for better compression (from PNG if needed)
};

function FileUploader({ 
  label, 
  onUploadComplete, 
  onRemove,
  multiple = false, 
  initialFiles = [], 
  minFiles = 1,
  className = '',
  enableCompression = true,         // Enable/disable compression
  compressionOptions = {}           // Custom compression options
}) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState('');
  const fileInputRef = useRef(null);

  // Merge default compression options with user-provided ones
  const compressionConfig = { ...DEFAULT_COMPRESSION_OPTIONS, ...compressionOptions };

  // Compress a single image file with aggressive optimization
  const compressImage = async (file) => {
    console.log(`🔍 Starting compression check for: ${file.name}`);
    console.log(`📊 enableCompression: ${enableCompression}`);
    console.log(`🖼️ File type: ${file.type}`);
    console.log(`📁 Original file size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
    
    if (!enableCompression || !file.type.startsWith('image/')) {
      console.log(`⏭️ Skipping compression - enableCompression: ${enableCompression}, isImage: ${file.type.startsWith('image/')}`);
      return file; // Return original file if compression is disabled or file is not an image
    }

    try {
      console.log(`🗜️ Compressing image: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
      console.log(`📋 Compression config:`, compressionConfig);
      
      // Store original file info for filename preservation
      const originalName = file.name;
      const originalType = file.type;
      
      // First pass: Aggressive compression with size target
      let compressedFile = await imageCompression(file, {
        ...compressionConfig,
        maxSizeMB: compressionConfig.maxSizeMB,
        quality: compressionConfig.initialQuality || 0.9
      });

      // Preserve original filename and type
      if (compressedFile.name !== originalName) {
        compressedFile = new File([compressedFile], originalName, {
          type: compressedFile.type || originalType,
          lastModified: Date.now()
        });
      }

      console.log(`📊 First pass result: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`);

      // If still too large, apply second pass with lower quality
      if (compressedFile.size > compressionConfig.maxSizeMB * 1024 * 1024) {
        console.log(`🔄 File still large (${(compressedFile.size / 1024 / 1024).toFixed(2)} MB), applying second compression pass...`);
        const secondPassConfig = {
          ...compressionConfig,
          maxSizeMB: compressionConfig.maxSizeMB * 0.8, // Even more aggressive
          quality: compressionConfig.quality * 0.9,      // Slightly lower quality
          maxWidthOrHeight: compressionConfig.maxWidthOrHeight * 0.9 // Smaller dimensions
        };
        console.log(`📋 Second pass config:`, secondPassConfig);
        
        compressedFile = await imageCompression(compressedFile, secondPassConfig);
        
        // Preserve original filename after second pass
        if (compressedFile.name !== originalName) {
          compressedFile = new File([compressedFile], originalName, {
            type: compressedFile.type || originalType,
            lastModified: Date.now()
          });
        }
        
        console.log(`📊 Second pass result: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`);
      }

      // If STILL too large, apply third pass with very aggressive settings
      if (compressedFile.size > compressionConfig.maxSizeMB * 1024 * 1024) {
        console.log(`🔄 File still large (${(compressedFile.size / 1024 / 1024).toFixed(2)} MB), applying final compression pass...`);
        const thirdPassConfig = {
          ...compressionConfig,
          maxSizeMB: compressionConfig.maxSizeMB * 0.6, // Very aggressive size limit
          quality: 0.7,                                  // Lower quality for final pass
          maxWidthOrHeight: Math.min(compressionConfig.maxWidthOrHeight * 0.8, 1200) // Smaller dimensions
        };
        console.log(`📋 Third pass config:`, thirdPassConfig);
        
        compressedFile = await imageCompression(compressedFile, thirdPassConfig);
        
        // Preserve original filename after third pass
        if (compressedFile.name !== originalName) {
          compressedFile = new File([compressedFile], originalName, {
            type: compressedFile.type || originalType,
            lastModified: Date.now()
          });
        }
        
        console.log(`📊 Third pass result: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`);
      }
      
      const originalSizeMB = file.size / 1024 / 1024;
      const compressedSizeMB = compressedFile.size / 1024 / 1024;
      const reduction = ((originalSizeMB - compressedSizeMB) / originalSizeMB * 100);
      
      console.log(`✅ Compression complete: ${compressedFile.name}`);
      console.log(`📊 Size: ${originalSizeMB.toFixed(2)}MB → ${compressedSizeMB.toFixed(2)}MB`);
      console.log(`📈 Reduction: ${reduction.toFixed(1)}% (${(originalSizeMB - compressedSizeMB).toFixed(2)}MB saved)`);
      console.log(`📁 Final filename: ${compressedFile.name} (preserved: ${compressedFile.name === originalName})`);
      
      return compressedFile;
    } catch (error) {
      console.error('❌ Image compression failed:', error);
      console.log('📋 Falling back to original file');
      return file; // Fallback to original file if compression fails
    }
  };

  // Compress multiple files
  const compressFiles = async (selectedFiles) => {
    if (!enableCompression) {
      return selectedFiles;
    }

    setCompressing(true);
    const compressedFiles = [];
    const totalFiles = selectedFiles.length;

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      setCompressionProgress(`Compressing ${i + 1} of ${totalFiles}: ${file.name}`);
      
      try {
        const compressedFile = await compressImage(file);
        compressedFiles.push(compressedFile);
      } catch (error) {
        console.error(`❌ Failed to compress ${file.name}:`, error);
        compressedFiles.push(file); // Add original file if compression fails
      }
    }

    setCompressing(false);
    setCompressionProgress('');
    return compressedFiles;
  };

  // Sync internal state with initialFiles prop (for edit mode or after uploads)
  useEffect(() => {
    console.log('🔄 FileUploader: Syncing with initialFiles:', initialFiles);
    
    // Ensure initialFiles is an array before processing
    const filesArray = Array.isArray(initialFiles) ? initialFiles : [];
    
    // Convert initialFiles to display objects compatible with FileUploader
    const convertedInitialFiles = filesArray.map((item, index) => {
      if (typeof item === 'object' && item.id) {
        // Item is already an image object with URL
        return {
          id: item.id,
          url: item.url || item.storage_path || `/media/${item.id}`,
          name: item.caption || item.alt_text || `Image ${index + 1}`,
          isUploaded: true,
          mediaId: item.id,
          ...item // Include all original properties
        };
      } else if (typeof item === 'string') {
        // Item is a URL or media ID
        const isUrl = item.startsWith('http') || item.startsWith('/');
        return {
          id: isUrl ? generateSimpleId() : item,
          url: isUrl ? item : `/media/${item}`,
          name: isUrl ? item.split('/').pop() : `Image ${index + 1}`,
          isUploaded: true,
          mediaId: isUrl ? null : item
        };
      } else {
        // Fallback for unknown format
        return {
          id: generateSimpleId(),
          url: `https://placehold.co/150x100/cccccc/000000?text=Image+${index + 1}`,
          name: `Image ${index + 1}`,
          isUploaded: true,
          mediaId: null
        };
      }
    });
    
    console.log('📷 FileUploader: Converted files for display:', convertedInitialFiles);
    setFiles(convertedInitialFiles);
  }, [initialFiles]);

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    setUploading(true);
    
    try {
      // Log original file information
      console.log('📁 FileUploader: Selected files:', selectedFiles.map(f => ({
        name: f.name, 
        size: `${(f.size / 1024 / 1024).toFixed(2)} MB`, 
        type: f.type
      })));

      // Compress files if compression is enabled
      const processedFiles = await compressFiles(selectedFiles);
      
      // Log compressed file information
      if (enableCompression) {
        console.log('�️ FileUploader: Compressed files:', processedFiles.map(f => ({
          name: f.name, 
          size: `${(f.size / 1024 / 1024).toFixed(2)} MB`, 
          type: f.type
        })));
      }
      
      // Call the parent's upload handler with processed (potentially compressed) files
      await onUploadComplete(processedFiles);
      console.log('✅ FileUploader: Upload completed successfully');
    } catch (error) {
      console.error('❌ FileUploader: Upload failed:', error);
    }
    
    setUploading(false);
    if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Clear input for next upload
    }
  };

  const handleRemoveFile = (fileToRemove) => {
    console.log('🗑️ FileUploader: Removing file:', fileToRemove);
    
    // Remove from local display
    const updatedFiles = files.filter(f => f.id !== fileToRemove.id);
    setFiles(updatedFiles);
    
    // Call parent's remove handler if provided
    if (onRemove) {
      const mediaId = fileToRemove.mediaId || fileToRemove.id;
      console.log('🗑️ FileUploader: Calling parent onRemove with:', mediaId);
      onRemove(mediaId);
    }
  };

  const currentDisplayFiles = Array.isArray(files) ? files : (files ? [files] : []);

  return (
    <div className={`mb-4 ${className}`}>
      {label && (
        <label className="block text-gray-700 text-sm font-semibold mb-2">
          {label}
        </label>
      )}
      <input
        type="file"
        multiple={multiple}
        accept="image/*"  // Accept only image files
        onChange={handleFileChange}
        className="hidden"
        ref={fileInputRef}
        id={`file-upload-${label.replace(/\s/g, '')}-${generateSimpleId()}`} // Unique ID for input
      />
      <Button
        type="button"
        onClick={() => fileInputRef.current.click()}
        className="bg-indigo-500 hover:bg-indigo-600 text-white inline-flex items-center"
        disabled={uploading || compressing}
        variant="primary"
      >
        {compressing ? 'Compressing...' : 
         uploading ? 'Uploading...' : 
         (multiple ? 'Choose Files' : 'Choose File')}
      </Button>

      <div className="mt-4">
        {(uploading || compressing) && (
          <div className="text-center py-8 border-2 border-dashed border-blue-300 rounded-lg bg-blue-50">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-blue-600 text-sm font-medium">
                {compressing ? 'Optimizing images...' : 'Processing files...'}
              </p>
            </div>
            {enableCompression && compressing && (
              <div className="mt-3">
                <p className="text-blue-500 text-xs">
                  High-efficiency compression: Reducing file size by up to 80%
                </p>
                {compressionProgress && (
                  <p className="text-blue-600 text-xs mt-1 font-mono">
                    {compressionProgress}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {currentDisplayFiles.length === 0 && !uploading && !compressing && (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <p className="text-gray-500 text-sm">No images uploaded yet.</p>
            <p className="text-gray-400 text-xs mt-1">
              {minFiles > 1 ? `Minimum ${minFiles} images required` : 'At least 1 image required'}
            </p>
          </div>
        )}
        
        {currentDisplayFiles.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {currentDisplayFiles.map((file) => (
              <div key={file.id} className="relative group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Image Preview */}
                <div className="aspect-square relative">
                  <img
                    src={file.url}
                    alt={file.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://placehold.co/150x150/cccccc/666666?text=IMG';
                    }}
                  />
                  
                  {/* Upload Status Overlay */}
                  {!file.isUploaded && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                  
                  {/* Remove Button */}
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(file)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    title="Remove image"
                  >
                    <XCircleIcon className="h-4 w-4" />
                  </button>
                  
                  {/* Success Indicator */}
                  {file.isUploaded && (
                    <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 bg-white rounded-full" />
                    </div>
                  )}
                </div>
                
                {/* File Info */}
                <div className="p-2">
                  <p className="text-xs text-gray-600 truncate" title={file.name}>
                    {file.name}
                  </p>
                  {file.file_size_bytes && (
                    <p className="text-xs text-gray-400">
                      {(file.file_size_bytes / 1024 / 1024).toFixed(1)} MB
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Summary Info */}
        {currentDisplayFiles.length > 0 && (
          <div className="mt-4 flex justify-between items-center text-sm">
            <span className="text-gray-600">
              {currentDisplayFiles.length} image{currentDisplayFiles.length !== 1 ? 's' : ''} uploaded
            </span>
            {minFiles > currentDisplayFiles.length && (
              <span className="text-red-500">
                {minFiles - currentDisplayFiles.length} more needed
              </span>
            )}
            {currentDisplayFiles.length >= minFiles && (
              <span className="text-green-600">✓ Minimum requirement met</span>
            )}
          </div>
        )}
      </div>
      <p className="text-xs text-gray-500 mt-2">
        {enableCompression ? 
          `Images will be aggressively compressed (max ${compressionConfig.maxSizeMB}MB, ${compressionConfig.maxWidthOrHeight}px) with multi-pass optimization for maximum size reduction.` :
          'Files will be uploaded to cloud storage when you select them.'
        }
      </p>
    </div>
  );
}

export default FileUploader;